// Settings › Activity (Console D, views/settings.js `#/settings/activity`; APS-320): the
// organization's audit log, newest first (owners and admins; plan step 2.1), filtered by who
// acted: people, agents (access tokens) or Apsio (staff and the system).
// What Apsio staff did or looked at shows here too, as staff (D56): "Apsio staff viewed your
// organization". Staff's reasons stay in the admin console; the before and after of a change
// (a plan, a trial's end) is shown. Actions without copy show their code, never a guess.
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { fetchAudit, membersQuery, orgQuery, projectsQuery } from '../api/queries'
import type { AuditEntry, Member, Plan, Project } from '../api/types'
import { Btn, EmptyNote, PageHead, Section, Seg, Table, TableFoot } from '../components/d'
import { Icon } from '../components/icons'
import { ErrorState, Loading } from '../components/states'
import { PLAN_KEY } from '../components/TrialMeter'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { initials } from '../lib/format'

/** The sentence for each action the log records; staff actions first (D56). */
export const ACTION_KEY: Record<string, MessageId> = {
  'organization.viewed': 'activity.staff.viewed',
  'organization.viewed_as_customer': 'activity.staff.viewedAsYou',
  'project.viewed_as_customer': 'activity.staff.viewedProject',
  'audit_log.viewed_as_customer': 'activity.staff.viewedLog',
  'organization.trial_extended': 'activity.staff.trialExtended',
  'organization.plan_changed': 'activity.staff.planChanged',
  'ingest.suspended': 'activity.staff.suspended',
  'ingest.restored': 'activity.staff.restored',
  'ingest.kill_switch_on': 'activity.staff.killOn',
  'ingest.kill_switch_off': 'activity.staff.killOff',
  'ingest.capture_override_set': 'activity.staff.captureSet',
  'billing.credit_applied': 'activity.staff.credited',
  'billing.invoice_refunded': 'activity.staff.refunded',
  'billing.subscription_canceled': 'activity.staff.canceledNow',
  'ingest.capture_override_cleared': 'activity.staff.captureCleared',
  'ingest.control_expired': 'activity.system.controlExpired',
  'organization.erasure_scheduled': 'activity.staff.erasureScheduled',
  'organization.erasure_canceled': 'activity.staff.erasureCanceled',
  'organization.erasure_aborted': 'activity.staff.erasureCanceled',
  'organization.erasure_retried': 'activity.staff.erasureRetried',
  'app_user.erasure_scheduled': 'activity.appUser.scheduled',
  'app_user.erasure_canceled': 'activity.appUser.canceled',
  'app_user.erasure_aborted': 'activity.appUser.canceled',
  'app_user.erasure_retried': 'activity.appUser.retried',
  'app_user.erased': 'activity.appUser.erased',
  'member.made_owner': 'activity.staff.madeOwner',
  'organization.created': 'activity.org.created',
  'organization.renamed': 'activity.org.renamed',
  'member.added': 'activity.member.added',
  'member.removed': 'activity.member.removed',
  'member.role_changed': 'activity.member.roleChanged',
  'invitation.created': 'activity.invitation.created',
  'invitation.revoked': 'activity.invitation.revoked',
  'invitation.accepted': 'activity.invitation.accepted',
  'project.created': 'activity.project.created',
  'project.renamed': 'activity.project.renamed',
  'app.created': 'activity.app.created',
  'app_key.created': 'activity.key.created',
  'app_key.rotated': 'activity.key.rotated',
  'app_key.revoked': 'activity.key.revoked',
  'agent.call': 'activity.agent.call',
  'alert.destination_created': 'activity.alert.destinationCreated',
  'alert.destination_updated': 'activity.alert.destinationUpdated',
  'alert.destination_deleted': 'activity.alert.destinationDeleted',
  'alert.destination_secret_rotated': 'activity.alert.secretRotated',
  'alert.destination_tested': 'activity.alert.tested',
  'alert.destination_confirmation_sent': 'activity.alert.confirmationSent',
  'alert.destination_email_confirmed': 'activity.alert.emailConfirmed',
  'alert.destination_email_unsubscribed': 'activity.alert.emailUnsubscribed',
  'alert.destination_email_bounced': 'activity.alert.emailBounced',
  'alert.destination_email_complained': 'activity.alert.emailComplained',
  'alert.rule_created': 'activity.alert.ruleCreated',
  'alert.rule_updated': 'activity.alert.ruleUpdated',
  'alert.rule_deleted': 'activity.alert.ruleDeleted',
  'billing.currency_switch_scheduled': 'activity.billing.switchScheduled',
  'billing.currency_switch_prepared': 'activity.billing.switchPrepared',
  'billing.currency_switched': 'activity.billing.switched',
  'billing.currency_switched_now': 'activity.billing.switched',
  'billing.switch_canceled': 'activity.billing.switchCanceled',
  'billing.subscribed': 'activity.billing.subscribed',
  'billing.plan_started': 'activity.billing.planStarted',
  'billing.subscription_ended': 'activity.billing.ended',
  'billing.payment_failed': 'activity.billing.paymentFailed',
  'organization.spend_cap_changed': 'activity.org.spendCap',
  'organization.trial_ended': 'activity.org.trialEnded',
  'usage.fair_use_changed': 'activity.org.fairUse',
  'query.run': 'activity.query.run',
  'project_token.create': 'activity.token.created',
  'project_token.revoke': 'activity.token.revoked',
  'agent_grant.create': 'activity.agent.granted',
  'project.updated': 'activity.project.updated',
  'project.in_app_updated': 'activity.project.inAppUpdated',
  'project.erasure_scheduled': 'activity.project.erasureScheduled',
  'project.erasure_canceled': 'activity.project.erasureCanceled',
  'project.erasure_aborted': 'activity.project.erasureCanceled',
  'project.erasure_retried': 'activity.project.erasureRetried',
  'project.erased': 'activity.project.erased',
  'instrumentation.update': 'activity.instrumentation.updated',
  'device_request.create': 'activity.device.requested',
  'device_request.cancel': 'activity.device.canceled',
  'live_tester.add': 'activity.live.testerAdded',
  'live_tester.remove': 'activity.live.testerRemoved',
  'live.view.start': 'activity.live.viewOpened',
  'device_command.send': 'activity.live.commandSent',
  'device_command.result': 'activity.live.commandAnswered',
  'app_user_pin.created': 'activity.pin.created',
  'app_user_pin.renamed': 'activity.pin.renamed',
  'app_user_pin.deleted': 'activity.pin.deleted',
  'device_request.replace': 'activity.device.replaced',
  'export.create': 'activity.export.created',
  'export.links': 'activity.export.links',
  'symbol_upload_token.create': 'activity.symbols.tokenCreated',
  'symbol_upload_token.revoke': 'activity.symbols.tokenRevoked',
  'instrumentation.layers_update': 'activity.instrumentation.rolloutsUpdated',
  'instrumentation.layer_stop': 'activity.instrumentation.rolloutStopped',
  'instrumentation.layer_rolled_back': 'activity.instrumentation.rolloutRolledBack',
  'billing.payment_method_changed': 'activity.billing.paymentMethodChanged',
  'organization.entitlements_changed': 'activity.staff.entitlementsChanged',
  'agent_grant.revoke': 'activity.agent.revoked',
  'agent_grant.expire': 'activity.agent.expired',
  'handoff.enabled': 'activity.handoff.enabled',
  'handoff.disabled': 'activity.handoff.disabled',
  'instrumentation.kill_switch_on': 'activity.instrumentation.killSwitchOn',
  'instrumentation.kill_switch_off': 'activity.instrumentation.killSwitchOff',
  'usage.capped': 'activity.usage.capped',
  'usage.cap_lifted': 'activity.usage.capLifted',
  'usage.replay_capped': 'activity.usage.replayCapped',
  'usage.replay_cap_lifted': 'activity.usage.replayCapLifted',
  'handoff.connected': 'activity.handoff.connected',
  'handoff.destination_created': 'activity.handoff.destinationCreated',
  'issue.assign': 'activity.issue.assigned',
  'issue.merge': 'activity.issue.merged',
  'issue.unmerge': 'activity.issue.unmerged',
  'issue.handoff': 'activity.issue.handedOff',
  'issue.regress': 'activity.issue.regressed',
  'issue.unignore': 'activity.issue.unignored',
  'issue.fix_version': 'activity.issue.fixVersion',
  'issue.resolve': 'activity.issue.resolved',
  'issue.unresolve': 'activity.issue.reopened',
  'issue.ignore': 'activity.issue.ignored',
}

/**
 * The grey line under an action (APS-396): its sentence, or for an action without one, what the
 * row itself says for sure, by who acted (Apsio staff, Apsio on its own, or someone else).
 */
export function actionLine(e: AuditEntry, i: Pick<Translator, 't' | 'date' | 'number'>): string {
  if (ACTION_KEY[e.action]) return describe(e, i)
  if (e.actor_kind === 'staff') return i.t('activity.generic.staff')
  if (e.actor_kind === 'system') return i.t('activity.generic.system')
  return i.t('activity.generic.other')
}

const PLANS = new Set<string>(['free', 'trial', 'pro', 'enterprise'])

const after = (e: AuditEntry): Record<string, unknown> => {
  const value = e.metadata.after
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

/** The sentence for one entry, with the values it needs from the entry's before and after. */
export function describe(e: AuditEntry, i: Pick<Translator, 't' | 'date' | 'number'>): string {
  const key = ACTION_KEY[e.action]
  if (!key) return e.action
  if (e.action === 'member.removed')
    return i.t(e.metadata.by_self === true ? 'activity.member.left' : key)
  if (e.action === 'billing.subscription_canceled')
    return i.t(e.metadata.when === 'period_end' ? 'activity.staff.canceledAtEnd' : key)
  if (e.action === 'member.role_changed') {
    const role = (r: unknown) =>
      r === 'owner' || r === 'admin' || r === 'member' ? i.t(`role.${r}`) : '—'
    return i.t(key, { from: role(e.metadata.from), to: role(e.metadata.to) })
  }
  if (e.action === 'organization.trial_extended') {
    const end = after(e).trial_ends_at
    return i.t(key, {
      date: typeof end === 'string' ? i.date(end, { dateStyle: 'medium' }) : '—',
    })
  }
  if (e.action === 'app_user.erasure_scheduled') {
    // A member's own entry has it in its metadata; staff's in the change's after.
    const at = e.metadata.execute_after ?? after(e).execute_after
    return i.t(key, {
      date: typeof at === 'string' ? i.date(at, { dateStyle: 'medium', timeStyle: 'short' }) : '—',
    })
  }
  if (e.action === 'app_user.erased') {
    const sessions = e.metadata.sessions
    return i.t(key, { sessions: typeof sessions === 'number' ? sessions : 0 })
  }
  if (e.action === 'organization.erasure_scheduled') {
    const at = after(e).execute_after
    return i.t(key, {
      date: typeof at === 'string' ? i.date(at, { dateStyle: 'medium' }) : '—',
    })
  }
  if (e.action === 'organization.plan_changed') {
    const plan = after(e).plan
    return i.t(key, {
      plan: typeof plan === 'string' && PLANS.has(plan) ? i.t(PLAN_KEY[plan as Plan]) : '—',
    })
  }
  if (e.action === 'ingest.capture_override_set') {
    const rate = after(e).capture_rate
    return i.t(key, {
      rate:
        typeof rate === 'number'
          ? i.number(rate, { style: 'percent', maximumFractionDigits: 1 })
          : '—',
    })
  }
  return i.t(key)
}

/** A person's call through an agent (the MCP server or an OAuth app): the agent acted, for them. */
const agentVia = (e: AuditEntry): 'mcp' | 'oauth' | undefined =>
  e.actor_kind === 'user' && (e.metadata.via === 'mcp' || e.metadata.via === 'oauth')
    ? e.metadata.via
    : undefined

/** The person behind a row: a member's name, or a former member. */
function person(e: AuditEntry, members: Member[] | undefined, t: Translator['t']): string {
  const member = members?.find((m) => m.user_id === e.actor_user_id)
  return member ? member.name || member.email : t('activity.who.former')
}

export function who(e: AuditEntry, members: Member[] | undefined, t: Translator['t']): string {
  if (e.actor_kind === 'staff') return t('activity.who.staff')
  if (e.actor_kind === 'system') return t('activity.who.system')
  if (e.actor_kind === 'token') return t('activity.who.token')
  return person(e, members, t)
}

const str = (value: unknown) => (typeof value === 'string' && value ? value : undefined)

/**
 * How the actor reached Apsio, the grey line under the actor (APS-396). An agent's call (the MCP
 * server or an OAuth app; the agent is the actor) names the channel and the person it acted for; a
 * project token, its token; a person, the console. A person's row without `via` is the console's:
 * the settings routes take the signed-in session only, and every agent path records its `via`.
 * Apsio's own rows have none.
 */
export function via(
  e: AuditEntry,
  members: Member[] | undefined,
  t: Translator['t'],
): string | undefined {
  const agent = agentVia(e)
  if (agent)
    return `${t(agent === 'mcp' ? 'agents.via.mcp' : 'agents.via.oauth')} · ${t('activity.via.for', { name: person(e, members, t) })}`
  if (e.actor_kind === 'token') {
    const id = str(e.metadata.token_id)
    return id
      ? `${t('agents.via.token')} · ${t('agents.tokenEnd', { id: id.slice(0, 8) })}`
      : t('agents.via.token')
  }
  if (e.actor_kind !== 'user') return undefined
  const how = e.metadata.via
  return how === undefined || how === 'console' ? t('activity.via.console') : undefined
}

/** A long id cut to what tells it apart, as the console's other short ids. */
const shortId = (id: string) => (id.length > 12 ? id.slice(0, 8) : id)

export type Target = { name: string } | { code: string; title: string }

/**
 * What an entry acted on, by name when the row or the page knows it (APS-396): a member, a
 * project, the organization, a project token, an alert destination's host, an OAuth client.
 * Anything else, or something gone since, shows as its kind and a short id, never a guess.
 */
export function target(
  e: AuditEntry,
  known: {
    members?: Member[] | undefined
    projects?: Project[] | undefined
    org?: { id: string; name: string } | undefined
  },
): Target {
  const name = targetName(e, known)
  return name
    ? { name }
    : { code: `${e.target_type} ${shortId(e.target_id)}`, title: `${e.target_type} ${e.target_id}` }
}

function targetName(e: AuditEntry, known: Parameters<typeof target>[1]): string | undefined {
  switch (e.target_type) {
    case 'user': {
      const m = known.members?.find((x) => x.user_id === e.target_id)
      return m ? m.name || m.email : undefined
    }
    case 'project':
      return known.projects?.find((p) => p.id === e.target_id)?.name ?? str(e.metadata.name)
    case 'organization':
      return known.org?.id === e.target_id ? known.org.name : undefined
    case 'project_token':
      return str(e.metadata.name)
    case 'alert_destination':
      return str(e.metadata.host)
    case 'agent_grant':
      return str(e.metadata.client_name)
    default:
      return undefined
  }
}

/** Who acted, as the filter groups them: people, agents (MCP, OAuth apps, project tokens), Apsio. */
type Actors = 'all' | 'people' | 'agents' | 'apsio'
export function actorGroup(e: AuditEntry): Exclude<Actors, 'all'> {
  if (e.actor_kind === 'staff' || e.actor_kind === 'system') return 'apsio'
  return e.actor_kind === 'token' || agentVia(e) ? 'agents' : 'people'
}

/** The actor as the mock draws it: a person's initials, or a mark for a token or for Apsio. */
function Actor({ e, members }: { e: AuditEntry; members: Member[] | undefined }) {
  const { t } = useI18n()
  const how = via(e, members, t)
  const line = how ? <span className="t2">{how}</span> : null
  // An agent's call: the agent is the actor, by the name its client gave (APS-396, Mosaic).
  const name = agentVia(e)
    ? (str(e.metadata.client) ?? t('activity.who.agent'))
    : who(e, members, t)
  if (e.actor_kind === 'user' && !agentVia(e)) {
    const m = members?.find((x) => x.user_id === e.actor_user_id)
    return (
      <>
        <span className="se-who">
          {/* A former member: a neutral dash, never a question mark (it reads as an error). */}
          <span className={m ? 'avatar' : 'avatar gone'} aria-hidden="true">
            {m ? initials(m.name, m.email) : '–'}
          </span>
          {name}
        </span>
        {line}
      </>
    )
  }
  return (
    <>
      <span className="se-who">
        <span className="se-ag" aria-hidden="true">
          <Icon name={e.actor_kind === 'staff' || e.actor_kind === 'system' ? 'shield' : 'agent'} />
        </span>
        {name}
      </span>
      {line}
    </>
  )
}

function TargetCell({ value }: { value: Target }) {
  return 'name' in value ? (
    value.name
  ) : (
    <code translate="no" title={value.title}>
      {value.code}
    </code>
  )
}

export function ActivityPage({
  orgId,
  crumb,
  title,
}: {
  orgId: string
  crumb: string
  title: string
}) {
  const i = useI18n()
  const { t, date, relative } = i
  const org = useQuery(orgQuery(orgId))
  const members = useQuery(membersQuery(orgId))
  const projects = useQuery(projectsQuery(orgId))
  const known = { members: members.data, projects: projects.data, org: org.data }
  const [actors, setActors] = useState<Actors>('all')
  const log = useInfiniteQuery({
    queryKey: ['org', orgId, 'audit'],
    queryFn: ({ pageParam }) => fetchAudit(orgId, pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => (last.length === 50 ? last.at(-1)?.id : undefined),
  })
  const entries = log.data?.pages.flat() ?? []
  const shown = actors === 'all' ? entries : entries.filter((e) => actorGroup(e) === actors)
  return (
    <main id="main" className="pg se-pg activity">
      <PageHead
        crumb={crumb}
        title={title}
        meta={org.data ? t('activity.meta', { org: org.data.name }) : undefined}
      />
      <Section
        title={t('activity.log')}
        meta={t('activity.logMeta')}
        right={
          <Seg
            label={t('activity.filter')}
            value={actors}
            onChange={setActors}
            options={[
              { value: 'all', label: t('activity.filter.all') },
              { value: 'people', label: t('activity.filter.people') },
              { value: 'agents', label: t('activity.filter.agents') },
              { value: 'apsio', label: t('activity.filter.apsio') },
            ]}
          />
        }
      >
        {log.isPending ? (
          <Loading />
        ) : log.isError ? (
          <ErrorState error={log.error} />
        ) : entries.length === 0 ? (
          <EmptyNote title={t('activity.none')} />
        ) : (
          <>
            {shown.length === 0 ? (
              <EmptyNote title={t('activity.noneHere')} />
            ) : (
              <Table label={t('table.of', { name: t('activity.log') })}>
                <thead>
                  <tr>
                    <th className="f">{t('activity.when')}</th>
                    <th>{t('activity.col.actor')}</th>
                    <th>{t('activity.col.action')}</th>
                    <th className="last">{t('activity.col.target')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((e) => (
                    <tr key={e.id} data-staff={e.actor_kind === 'staff' || undefined}>
                      <td className="f">
                        <time
                          className="faint"
                          dateTime={e.created_at}
                          title={date(e.created_at, { dateStyle: 'medium', timeStyle: 'short' })}
                        >
                          {relative(e.created_at)}
                        </time>
                      </td>
                      <td>
                        <Actor e={e} members={members.data} />
                      </td>
                      <td>
                        <code translate="no">{e.action}</code>
                        <span className="t2 prose">{actionLine(e, i)}</span>
                      </td>
                      <td className="last">
                        <TargetCell value={target(e, known)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {/* The mock's foot: what the log never keeps, and older entries while there are any. */}
            <TableFoot>
              <span>{t('activity.foot')}</span>
              {log.hasNextPage ? (
                <Btn
                  variant="ghost"
                  size="sm"
                  disabled={log.isFetchingNextPage}
                  onClick={() => log.fetchNextPage()}
                >
                  {t('activity.older')}
                </Btn>
              ) : null}
            </TableFoot>
          </>
        )}
      </Section>
    </main>
  )
}
